/**
 * AddMailingBalanceModal - Stripe checkout for adding to mailing balance
 * Simplified version of PurchaseProductModal for dollar amount selection
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { getPaymentMethods } from '@/api/paymentMethods'
import type { PaymentMethod } from '@/types/paymentMethod'
import SavedCardSelector from '@/features/wallet/SavedCardSelector'
import { initiateAddBalance, confirmAddBalance } from '@/services/mailingBalanceService'

const PRESET_AMOUNTS = [10, 25, 50, 100]

interface AddMailingBalanceModalProps {
  isOpen: boolean
  currentBalance: number
  onClose: () => void
  onSuccess: (newBalance: number) => void
}

export function AddMailingBalanceModal({ isOpen, currentBalance, onClose, onSuccess }: AddMailingBalanceModalProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [amount, setAmount] = useState(25)
  const [customAmount, setCustomAmount] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [savedCards, setSavedCards] = useState<PaymentMethod[]>([])
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [useNewCard, setUseNewCard] = useState(false)
  const [saveCard, setSaveCard] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'amount' | 'payment'>('amount')

  // Fetch saved cards on mount
  useEffect(() => {
    if (!isOpen) return
    const fetchCards = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) return
        const response = await getPaymentMethods(token)
        const cards = response.payment_methods || []
        setSavedCards(cards)
        if (cards.length > 0) {
          const defaultCard = cards.find((c: PaymentMethod) => c.is_default)
          setSelectedCardId(defaultCard?.id || cards[0].id)
          setUseNewCard(false)
        } else {
          setUseNewCard(true)
        }
      } catch {
        setUseNewCard(true)
      }
    }
    fetchCards()
  }, [isOpen])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setAmount(25)
      setCustomAmount('')
      setIsCustom(false)
      setError('')
      setStep('amount')
      setIsProcessing(false)
    }
  }, [isOpen])

  const finalAmount = isCustom ? parseFloat(customAmount) || 0 : amount

  const handleSelectPreset = (val: number) => {
    setAmount(val)
    setIsCustom(false)
    setError('')
  }

  const handleCustomChange = (val: string) => {
    setCustomAmount(val)
    setIsCustom(true)
    setError('')
  }

  const handleNextToPayment = () => {
    if (finalAmount < 5) {
      setError('Minimum amount is $5')
      return
    }
    if (finalAmount > 500) {
      setError('Maximum amount is $500')
      return
    }
    setError('')
    setStep('payment')
  }

  const handlePay = async () => {
    if (!stripe) return
    setIsProcessing(true)
    setError('')

    try {
      // Using saved card
      if (!useNewCard && selectedCardId) {
        const selectedCard = savedCards.find(c => c.id === selectedCardId)
        if (!selectedCard) {
          setError('Selected card not found')
          return
        }
        const result = await initiateAddBalance({
          amount: finalAmount,
          saved_payment_method_id: selectedCard.stripe_payment_method_id
        })

        if (result.success && result.balance !== undefined) {
          onSuccess(result.balance)
          return
        }

        // If not auto-confirmed, confirm on client side
        if (result.client_secret) {
          const { error: stripeError } = await stripe.confirmCardPayment(result.client_secret)
          if (stripeError) {
            setError(stripeError.message || 'Payment failed')
            return
          }
          const confirmed = await confirmAddBalance(result.payment_intent_id)
          onSuccess(confirmed.balance)
        }
        return
      }

      // Using new card
      const cardElement = elements?.getElement(CardElement)
      if (!cardElement) {
        setError('Please enter card details')
        return
      }

      const result = await initiateAddBalance({
        amount: finalAmount,
        save_payment_method: saveCard
      })

      if (!result.client_secret) {
        setError('Failed to create payment')
        return
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(result.client_secret, {
        payment_method: { card: cardElement }
      })

      if (stripeError) {
        setError(stripeError.message || 'Payment failed')
        return
      }

      if (paymentIntent?.status === 'succeeded') {
        const confirmed = await confirmAddBalance(result.payment_intent_id)
        onSuccess(confirmed.balance)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        {step === 'amount' ? (
          <>
            <h2 className="text-lg font-bold text-gray-800 mb-1">Add to Mailing Balance</h2>
            <p className="text-sm text-gray-400 mb-4">
              Current balance: <span className="font-semibold text-gray-600">${currentBalance.toFixed(2)}</span>
            </p>

            {/* Preset amounts */}
            <div className="grid grid-cols-4 gap-2 mb-3">
              {PRESET_AMOUNTS.map(val => (
                <button
                  key={val}
                  onClick={() => handleSelectPreset(val)}
                  className={`py-2 text-sm font-semibold rounded-lg border transition-colors ${
                    !isCustom && amount === val
                      ? 'bg-pink-500 text-white border-pink-500'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-pink-300'
                  }`}
                >
                  ${val}
                </button>
              ))}
            </div>

            {/* Custom amount */}
            <div className="relative mb-4">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                value={customAmount}
                onChange={e => handleCustomChange(e.target.value)}
                onFocus={() => setIsCustom(true)}
                placeholder="Custom amount (5-500)"
                min={5}
                max={500}
                step="1"
                className={`w-full pl-7 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent ${
                  isCustom ? 'border-pink-400' : 'border-gray-200'
                }`}
              />
            </div>

            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleNextToPayment}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-pink-500 rounded-xl hover:bg-pink-600 transition-colors"
              >
                Add ${finalAmount > 0 ? finalAmount.toFixed(2) : '0.00'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-bold text-gray-800 mb-1">Payment</h2>
            <p className="text-sm text-gray-400 mb-4">
              Adding <span className="font-semibold text-pink-600">${finalAmount.toFixed(2)}</span> to mailing balance
            </p>

            {/* Saved cards */}
            {savedCards.length > 0 && !useNewCard && (
              <div className="mb-3">
                <SavedCardSelector
                  paymentMethods={savedCards}
                  selectedId={selectedCardId}
                  onSelect={(id) => setSelectedCardId(id)}
                  onAddNew={() => setUseNewCard(true)}
                />
              </div>
            )}

            {/* New card input */}
            {(useNewCard || savedCards.length === 0) && (
              <div className="mb-3">
                {savedCards.length > 0 && (
                  <button
                    onClick={() => setUseNewCard(false)}
                    className="text-xs text-pink-500 hover:text-pink-600 mb-2"
                  >
                    Use saved card
                  </button>
                )}
                <div className="border border-gray-200 rounded-lg p-3">
                  <CardElement
                    options={{
                      style: {
                        base: { fontSize: '14px', color: '#374151' },
                        invalid: { color: '#ef4444' }
                      }
                    }}
                  />
                </div>
                <label className="flex items-center gap-2 mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={e => setSaveCard(e.target.checked)}
                    className="w-3.5 h-3.5 text-pink-500 rounded"
                  />
                  <span className="text-xs text-gray-500">Save card for future use</span>
                </label>
              </div>
            )}

            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={() => { setStep('amount'); setError('') }}
                disabled={isProcessing}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handlePay}
                disabled={isProcessing || !stripe}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-pink-500 rounded-xl hover:bg-pink-600 transition-colors disabled:opacity-50"
              >
                {isProcessing ? 'Processing...' : `Pay $${finalAmount.toFixed(2)}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}
